import { UseFormReturn } from "react-hook-form";
import { ProposeEventFormData } from "../useCreateEvent";
import GoogleMapsAutoComplete, {
    extractCityFromGooglePlace,
    extractCoordinates,
    GooglePlace,
    useGooglePlace,
} from "../../themed/GoogleMapsAutoComplete";
import { getTimezoneFromCoordinates } from "~/utils/timezones";
import { Label } from "../../shadcn/Label";
import { EventDate } from "../../events-components/EventViewAndEdit/EventDateTime";
import TimeInput from "../../themed/TimeInput";
import { useEffect } from "react";
import { mergeToTime } from "~/utils/time";

export const ExternalEventModalContent = ({
    form,
}: {
    form: UseFormReturn<ProposeEventFormData>;
}) => {
    const [googlePlace, setGooglePlace] = useGooglePlace(
        form.watch("googleMapsPlaceId"),
    );

    const { watch, setValue } = form;
    const startTime24h = watch("startTime24h");
    const endTime24h = watch("endTime24h");

    const isLumaEvent = () => {
        return form.watch("externalEventUrl")?.includes("lu.ma");
    };

    useEffect(() => {
        if (isLumaEvent()) return;
        const currentStartTime = form.getValues("startTime");
        if (startTime24h && currentStartTime) {
            const mergedStart = mergeToTime(startTime24h, currentStartTime);
            setValue("startTime", mergedStart, { shouldValidate: true });
        }
    }, [startTime24h, setValue, form]);

    useEffect(() => {
        if (isLumaEvent()) return;
        const currentEndTime = form.getValues("endTime");
        if (endTime24h && currentEndTime) {
            const mergedEnd = mergeToTime(endTime24h, currentEndTime);
            setValue("endTime", mergedEnd, { shouldValidate: true });
        }
    }, [endTime24h, setValue, form]);

    const onUpdateGoogleMaps = async (value: GooglePlace) => {
        const { longitude, latitude } = extractCoordinates(value?.geometry);

        const timezone =
            (await getTimezoneFromCoordinates(latitude ?? 0, longitude ?? 0)) ??
            "Etc/UTC";

        // timezone based on google place's lat long
        const updates = {
            timezone: timezone,
            city: extractCityFromGooglePlace(value),
            longitude,
            latitude,
            googleMapsPlaceId: value?.place_id ?? null,
            googleMapsPlaceName: value?.name ?? null,
            googleMapsPlaceTypes: value?.types ?? null,
            location: value.formatted_address ?? "",
            locationName: value?.name ?? null,
            useGoogleMapsLocation: !!value?.place_id,
            country: value?.address_components?.find((c) =>
                c.types.includes("country"),
            )?.long_name,
        };

        // set values
        for (const [k, v] of Object.entries(updates)) {
            // @ts-ignore
            form.setValue(k, v, { shouldDirty: true });
        }
        setGooglePlace(value);
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="z-[200] flex flex-col gap-2">
                <Label>Date:</Label>
                <EventDate
                    isEditing
                    isLockedMode={false}
                    value="startTime"
                    valueLocked="startTimeLocked"
                    timeField="startTime24h"
                    isViewModeEnabled={false}
                />
            </div>
            <div className="flex flex-col justify-between sm:flex-row">
                <div className="flex flex-1 flex-col gap-2">
                    <Label>Start:</Label>
                    <TimeInput
                        timeZone={form.watch("timezone")}
                        time={
                            form.watch("startTime")
                                ? form.watch("startTime")
                                : null
                        }
                        formField="startTime24h"
                        isEditMode={true}
                        required={true}
                    />
                </div>
                <div className="flex flex-1 flex-col gap-2">
                    <Label>End:</Label>
                    <TimeInput
                        timeZone={form.watch("timezone")}
                        time={
                            form.watch("endTime")
                                ? new Date(form.watch("endTime")!)
                                : null
                        }
                        formField="endTime24h"
                        isEditMode={true}
                    />
                </div>
            </div>

            <div className="flex flex-1 flex-col gap-2">
                <Label>Location:</Label>

                <GoogleMapsAutoComplete
                    label={false}
                    isEditing={true}
                    value={googlePlace}
                    setValue={(val) => onUpdateGoogleMaps(val)}
                    className="z-[100]"
                    required={true}
                />
            </div>
        </div>
    );
};
